// ============================================================================
// FileMultiField.tsx — ТЧ в виде «Поле» для КОЛЛЕКЦИИ ФАЙЛОВ (множественное
// значение): строки ТЧ = файлы; сведения (размер/загрузил/дата) — SSoT сервер
// (ListFiles, батч); отображение — ЕДИНЫЙ виджет FileCollection (вид и
// настройки — свойства элемента макета). «Добавить» — пикер файлов.
// ============================================================================

import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { FileCollection, type FileCollectionView, type FileDetail } from '@/components/collection/FileCollection';

interface FileMultiFieldProps {
  tsName: string;
  colName: string;
  rows: Array<Record<string, unknown>>;
  /** Вид виджета (свойство элемента ТЧ) */
  view: FileCollectionView;
  /** Настройки виджета (свойство элемента ТЧ) */
  settings?: Record<string, unknown>;
  onAdd: () => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  title?: string;
}

export function FileMultiField({ tsName, colName, rows, view, settings = {}, onAdd, onRemove, disabled, title }: FileMultiFieldProps) {
  // Сведения о файлах (SSoT — сервер, ListFiles): id/имя/размер/загрузил/дата
  const [details, setDetails] = useState<FileDetail[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getSDK().files.listFiles({})
      .then((r: any) => {
        if (cancelled) return;
        setDetails((r?.items || []).map((f: any) => ({
          id: f.id, fileName: f.fileName || '', fileSize: Number(f.fileSize || 0),
          fileType: f.fileType || '', uploadedBy: f.uploadedBy || '', uploadedAt: f.uploadedAt || '',
        })));
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Ошибка загрузки сведений о файлах'); });
    return () => { cancelled = true; };
  }, [tsName]);

  // Файлы из строк ТЧ (значение колонки = id файла)
  const ids = new Set(rows.map((r) => String(r[colName] ?? '')).filter(Boolean));
  const files = details.filter((f) => ids.has(f.id));
  // Строки, для которых ещё нет сведений — показываем имя из presentation
  const pending = rows
    .filter((r) => r[colName] && !files.some((f) => f.id === String(r[colName])))
    .map((r) => ({ id: String(r[colName]), fileName: String(r[colName + '_presentation'] ?? r[colName]), fileSize: 0, fileType: '' }));

  return (
    <div className="space-y-1 border rounded-lg p-3">
      <div className="flex items-center gap-2">
        {title && <span className="text-xs font-medium">{title}</span>}
        {!disabled && (
          <Button size="sm" className="h-6 px-2 text-xs ml-auto" onClick={onAdd}>
            <Plus className="h-3 w-3 mr-1" />Добавить
          </Button>
        )}
      </div>
      <FileCollection
        files={[...files, ...pending]}
        view={view}
        settings={settings as any}
        onPick={undefined}
        onRemove={disabled ? undefined : (f) => onRemove(f.id)}
        actions
        emptyText="Нет файлов"
      />
      {error && <div className="text-[10px] text-destructive">{error}</div>}
    </div>
  );
}
